import type { OfferWithRelations } from "./offer-types"

export type OfferParentMap = Map<string, string | null>

type OfferLink = Pick<OfferWithRelations, "id" | "parent_offer_id" | "status">

export function buildParentMapFromOffers(
  offers: Iterable<OfferLink>,
): OfferParentMap {
  const map: OfferParentMap = new Map()
  for (const o of Array.from(offers)) {
    map.set(o.id, o.parent_offer_id)
  }
  return map
}

export function negotiationDepthFromParentMap(
  offerId: string,
  parentById: OfferParentMap,
): number | null {
  if (!parentById.has(offerId)) return null
  let depth = 0
  let current: string | null = offerId
  const seen = new Set<string>()
  while (current) {
    if (seen.has(current)) return null
    seen.add(current)
    const parent = parentById.get(current)
    if (parent === undefined) return null
    if (parent === null) return depth
    depth += 1
    current = parent
  }
  return depth
}

export function isSellerResponseTurn(
  offer: OfferLink,
  offerMap: Map<string, OfferLink>,
): boolean {
  if (offer.status !== "pending") return false
  const parentById = buildParentMapFromOffers([...Array.from(offerMap.values()), offer])
  const depth = negotiationDepthFromParentMap(offer.id, parentById)
  return depth !== null && depth % 2 === 0
}

export function isBuyerResponseTurn(
  offer: OfferLink,
  offerMap: Map<string, OfferLink>,
): boolean {
  if (offer.status !== "pending") return false
  const parentById = buildParentMapFromOffers([...Array.from(offerMap.values()), offer])
  const depth = negotiationDepthFromParentMap(offer.id, parentById)
  return depth !== null && depth % 2 === 1
}

export function offerNeedsResponseFromUser(
  offer: OfferWithRelations,
  userId: string,
  offerMap: Map<string, OfferLink>,
): boolean {
  if (offer.seller_id === userId) return isSellerResponseTurn(offer, offerMap)
  if (offer.buyer_id === userId) return isBuyerResponseTurn(offer, offerMap)
  return false
}

export async function collectAncestorParentIds(
  seedIds: string[],
  fetchRows: (ids: string[]) => Promise<Array<{ id: string; parent_offer_id: string | null }>>,
): Promise<OfferParentMap> {
  const parentById: OfferParentMap = new Map()
  const known = new Set<string>()
  let frontier = Array.from(new Set(seedIds))

  while (frontier.length > 0) {
    const batch = frontier.filter((id) => !known.has(id))
    if (batch.length === 0) break
    for (const id of batch) known.add(id)

    const rows = await fetchRows(batch)
    const next: string[] = []

    for (const row of rows) {
      parentById.set(row.id, row.parent_offer_id)
      if (row.parent_offer_id && !known.has(row.parent_offer_id)) {
        next.push(row.parent_offer_id)
      }
    }

    frontier = next
  }

  return parentById
}
